import type * as React from 'react'
import { memo, useCallback } from 'react'
import type { JSX } from 'react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { CalendarSlotButton } from '~publish/components/Calendar/CalendarSlotButton'
import type { CalendarSlotButtonVariant } from '~publish/components/Calendar/CalendarSlotButton'
import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { trackEmptyStateClicked } from '~publish/helpers/queueCalendarTracking'
import { useCta } from '~publish/hooks/useCta'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { useQueryParam } from '~publish/hooks/useQueryParam'
import { useTimezone } from '~publish/hooks/useTimezone'

interface SlotItemProps extends React.HTMLAttributes<
  HTMLButtonElement | HTMLDivElement
> {
  timestamp: number
  channelId: string | null
  channelName?: string
  channelService?: Service
  isDraggingOver?: boolean
  is24HourFormat?: boolean
  isEmptyStateSlot?: boolean
  isValueLedEmptyStateSlot?: boolean
}

type SlotTriggerOptions = Pick<
  SlotItemProps,
  | 'timestamp'
  | 'channelId'
  | 'channelService'
  | 'isEmptyStateSlot'
  | 'isValueLedEmptyStateSlot'
>

interface SlotTrigger {
  clickHandler: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void
  triggerAttributes: ReturnType<typeof usePostComposer>['triggerAttributes']
}

function useSlotTrigger({
  timestamp,
  channelId,
  channelService,
  isEmptyStateSlot = false,
  isValueLedEmptyStateSlot = false,
}: SlotTriggerOptions): SlotTrigger {
  const organizationId = useOrganizationId()

  const {
    triggerAttributes,
    createNewPinnedPostInComposer,
    createNewPostInComposer,
  } = usePostComposer()
  const [tagsQueryParam] = useQueryParam<string[]>('tags')
  const composerCta = !channelId
    ? 'publish-calendar-valueLedEmptyState-previewSlot-1'
    : isValueLedEmptyStateSlot
      ? 'publish-calendar-valueLedEmptyState-slot-1'
      : 'publish-calendar-slot-addPost-1'
  const { clicked: trackClick } = useCta(composerCta)

  const openComposer = useCallback(
    (scheduledAt: number): void => {
      if (!channelId) {
        void createNewPostInComposer({
          cta: composerCta,
          channels: [],
          prefillPostData: {
            dueAt: Math.round(scheduledAt / 1000),
            tags: tagsQueryParam,
            pinned: true,
          },
        })
        return
      }

      void createNewPinnedPostInComposer({
        cta: composerCta,
        channels: [channelId],
        prefillPostData: {
          dueAt: Math.round(scheduledAt / 1000),
          tags: tagsQueryParam,
          pinned: true,
        },
      })
    },
    [
      channelId,
      composerCta,
      createNewPinnedPostInComposer,
      createNewPostInComposer,
      tagsQueryParam,
    ],
  )

  const clickHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      event.preventDefault()
      if (isValueLedEmptyStateSlot) trackClick()
      if (isEmptyStateSlot && organizationId && channelService) {
        trackEmptyStateClicked({
          organizationId,
          feature: 'calendar',
          channel: channelService,
        })
      }
      openComposer(timestamp)
    },
    [
      channelService,
      isEmptyStateSlot,
      isValueLedEmptyStateSlot,
      openComposer,
      organizationId,
      timestamp,
      trackClick,
    ],
  )

  return { clickHandler, triggerAttributes }
}

function SlotItemRaw({
  timestamp,
  channelId,
  channelName,
  channelService,
  isDraggingOver,
  className,
  is24HourFormat,
  isEmptyStateSlot,
  isValueLedEmptyStateSlot,
  variant,
  ...props
}: SlotItemProps & { variant?: CalendarSlotButtonVariant }): JSX.Element {
  const timezone = useTimezone()
  const { clickHandler, triggerAttributes } = useSlotTrigger({
    timestamp,
    channelId,
    channelService,
    isEmptyStateSlot,
    isValueLedEmptyStateSlot,
  })

  return (
    <CalendarSlotButton
      variant={variant}
      timestamp={timestamp}
      timezone={timezone}
      channelId={channelId}
      channelName={channelName}
      channelService={channelService}
      isDraggingOver={isDraggingOver}
      className={className}
      is24HourFormat={is24HourFormat}
      onClick={clickHandler}
      {...triggerAttributes}
      {...props}
    />
  )
}

function MonthSlotItemRaw(props: SlotItemProps): JSX.Element {
  return <SlotItemRaw {...props} variant="month" />
}

function WeekSlotItemRaw(props: SlotItemProps): JSX.Element {
  return <SlotItemRaw {...props} variant="week" />
}

export const SlotItem = memo(SlotItemRaw)

export const MonthSlotItem = memo(MonthSlotItemRaw)

export const WeekSlotItem = memo(WeekSlotItemRaw)
